import React, { useEffect, useMemo, useState } from "react";
import { Col, Row, Table, Button } from "reactstrap";
import { AiFillDelete, AiFillEdit, AiOutlineSearch } from "react-icons/ai";
import { Modal, Input } from "antd";

import SideBar from "../../components/SideBar";
import Header from "../../components/DataManagement/Document/Header";

import {
  getAllDocument,
  updateDocumentStatus,
  updateDocument,
  deleteOneDocument,
} from "./../../api/data/Documents";
import Swal from "sweetalert2";

function DocumentTable() {
  const [DocumentData, setDocumentData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [editModal, setEditModal] = useState(false);
  const [editData, setEditData] = useState(null);

  const getDocumentData = async () => {
    try {
      setIsLoading(true);
      setHasError(false);

      const response = await getAllDocument();

      if (response && !Array.isArray(response)) {
        setHasError(true);
        setDocumentData([]);
        return;
      }

      setDocumentData(Array.isArray(response) ? response : []);
    } catch (err) {
      setHasError(true);
      setDocumentData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return DocumentData;

    return DocumentData.filter(el =>
      [el.immatriculation, el.cartGris, el.vignette, el.assurance, el.visiteTechnique]
        .filter(Boolean)
        .some(field => field.toLowerCase().includes(term))
    );
  }, [DocumentData, searchTerm]);

  const handleEditClick = (el) => {
    setEditData({
      _id: el._id,
      immatriculation: el.immatriculation,
      cartGris: el.cartGris,
      vignette: el.vignette,
      assurance: el.assurance,
      visiteTechnique: el.visiteTechnique,
    });
    setEditModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditData({ ...editData, [name]: value });
  };

  const handleUpdateSubmit = async () => {
    try {
      const { _id, ...data } = editData;
      const response = await updateDocument(_id, data);
      if (response) {
        setEditModal(false);
        getDocumentData();
        Swal.fire("Modifié", "", "success");
      }
    } catch (err) {
      Swal.fire("Erreur", err?.response?.data?.error || "", "error");
    }
  };

  const handleStateChange = async (id, status) => {
    try {
      Swal.fire({
        title:
          status === "V"
            ? "Voulez-vous valider cette opération ?"
            : "Voulez-vous annuler cette opération ?",
        icon: "warning",
        confirmButtonColor: status === "V" ? "#198754" : "#dc3545",
        confirmButtonText: status === "V" ? "Valider" : "Annuler",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const response = await updateDocumentStatus(id, status);
            if (response) {
                getDocumentData()
                Swal.fire(status === "V" ? "Validée" : "Annulée", "", "success");
            }
          } catch (err) {
            Swal.fire("Erreur", "", "error");
          }
        }
      });
    } catch (err) {}
  };

  const handleDelete = async (id) => {
    try {
      Swal.fire({
        title: "Voulez-vous supprimer ce document ?",
        icon: "warning",
        confirmButtonColor: "#dc3545",
        confirmButtonText: "Supprimer",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const response = await deleteOneDocument(id);
            if (response) {
                getDocumentData()
                Swal.fire("Supprimé", "", "success");
            }
          } catch (err) {
            Swal.fire("Erreur", "", "error");
          }
        }
      });
    } catch (err) {}
  };

  useEffect(() => {
    getDocumentData();
  }, []);

  return (
    <section className="documentTableSection">
      <div className="contents">
        <Row>
          <Col md="2" className="p-0">
            <SideBar />
          </Col>
          <Col md="10" className="p-0">
            <Header />

            <div className="tableSection">
              <div className="tableToolbar">
                <Input
                  prefix={<AiOutlineSearch />}
                  placeholder="Rechercher une immatriculation, une assurance…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  allowClear
                />
              </div>

              <div className="table-wrapper">
                {isLoading && (
                  <p className="stateMessage">Chargement des documents…</p>
                )}

                {!isLoading && hasError && (
                  <p className="stateMessage error">Impossible de charger les documents. Réessayez dans un instant.</p>
                )}

                {!isLoading && !hasError && DocumentData.length === 0 && (
                  <p className="stateMessage">Aucun document enregistré. Cliquez sur « Ajouter un document » pour commencer.</p>
                )}

                {!isLoading && !hasError && DocumentData.length > 0 && filteredData.length === 0 && (
                  <p className="stateMessage">Aucun document ne correspond à « {searchTerm} ».</p>
                )}

                {!isLoading && !hasError && filteredData.length > 0 && (
                  <Table striped>
                    <thead>
                      <tr>
                        <th>Immatriculation</th>
                        <th>Carte grise</th>
                        <th>Vignette</th>
                        <th>Assurance</th>
                        <th>Visite technique</th>
                        <th>Etat d'opération</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.map((el) => (
                        <tr key={el._id}>
                          <td>{el.immatriculation}</td>
                          <td>{el.cartGris}</td>
                          <td>{el.vignette}</td>
                          <td>{el.assurance}</td>
                          <td>{el.visiteTechnique}</td>
                          <td>
                            {el.status === "N" && (
                              <div className="btnStatus">
                                <Button size="sm" color="success" onClick={() => handleStateChange(el._id, "V")}>Valider</Button>
                                <Button size="sm" color="danger" onClick={() => handleStateChange(el._id, "A")}>Annuler</Button>
                              </div>
                            )}
                            {el.status === "V" && <span className="status-badge badge-valid">✓ Validée</span>}
                            {el.status === "A" && <span className="status-badge badge-canceled">✕ Annulée</span>}
                          </td>
                          <td>
                            <div className="action-icons">
                              <button className="action-btn edit-btn" onClick={() => handleEditClick(el)}><AiFillEdit size="1rem" /></button>
                              <button className="action-btn delete-btn" onClick={() => handleDelete(el._id)}><AiFillDelete size="1rem" /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </div>

      <Modal
        title="Modifier le document"
        open={editModal}
        onOk={handleUpdateSubmit}
        onCancel={() => setEditModal(false)}
        okText="Enregistrer"
        cancelText="Annuler"
      >
        {editData && (
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <Input placeholder="Immatriculation" name="immatriculation" value={editData.immatriculation} onChange={handleEditChange} />
            <Input placeholder="Carte grise" name="cartGris" value={editData.cartGris} onChange={handleEditChange} />
            <Input placeholder="Vignette" name="vignette" value={editData.vignette} onChange={handleEditChange} />
            <Input placeholder="Assurance" name="assurance" value={editData.assurance} onChange={handleEditChange} />
            <Input placeholder="Visite technique" name="visiteTechnique" value={editData.visiteTechnique} onChange={handleEditChange} />
          </div>
        )}
      </Modal>
    </section>
  );
}

export default DocumentTable;
